import { useCallback, useMemo } from 'react';

import useGetProjectPublishedStatus from '@/connection/projects/use-get-project-published-status';
import { useAppSelector } from '@/store/hooks';
import { getWorkingProject } from '@/store/slices/project/selectors';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import { EPermissions } from '@/types/permissions.types';
import AuthWrapper from '@ui/auth-wrapper/auth-wrapper';
import { getBaseAdminURL } from '@/utils/urls';
import { Session } from '@/types/session.types';
import { useMatches } from 'react-router-dom';
import OptionalComponent from '@ui/optional-component/optional-component';

type TModerateButtonProps = {
  session?: Session;
};

const ModerateButton = ({ session }: TModerateButtonProps) => {
  const workingProject = useAppSelector(getWorkingProject);
  const matches = useMatches();

  useGetProjectPublishedStatus();

  const handleModerate = useCallback(() => {
    if (workingProject) {
      const _session = session ?? workingProject.sessions[0];
      const url = `${getBaseAdminURL()}/moderator/event/${workingProject.uuid}/${_session?.default_language}/session/${_session?.uuid}`;
      window.open(url, '_self');
    }
  }, [session, workingProject]);

  const isSessionRouteMatch = useMemo(() => !!matches.find((m) => m.id === 'edit-project-session'), [matches]);

  const isSessionPage = useMemo(() => {
    return workingProject?.sessions.length === 1 ? true : !!isSessionRouteMatch;
  }, [isSessionRouteMatch, workingProject?.sessions.length]);

  return (
    <OptionalComponent display={isSessionPage}>
      <AuthWrapper
        allowedRoles={[EPermissions.Owner, EPermissions.Admin, EPermissions.Moderation, EPermissions.GroupAdmin]}
      >
        <Button
          hoverBackgroundColor="var(--gray-700)"
          outlineOnFocus={false}
          className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10 keep-open"
          onClick={handleModerate}
        >
          <Icon name={ICONS.MODERATOR_2} />
          <Text color="var(--gray-100)" className="P2">
            Moderate show
          </Text>
        </Button>
      </AuthWrapper>
    </OptionalComponent>
  );
};

export default ModerateButton;
